import React, { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Paper, Radio, Step, StepLabel,
  Stepper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import {
  desvincularConciliacion, enlazarBancoConciliacion, enlazarTraspasoConciliacion, fmtFecha, fmtMoney, getCandidatosBanco,
  getCreditosTraspaso, getRelaciones, getTraspasosLibres,
} from "../services/egresosService";

const msg = (e, def) => e?.response?.data?.error || def;
const num = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// Tablas legibles: texto de 13 px, encabezado con fondo suave, números a la derecha y sin línea en la última fila.
const tablaSx = {
  "& .MuiTableCell-root": { py: 0.8, px: 1.25, fontSize: 13, whiteSpace: "nowrap" },
  "& thead .MuiTableCell-root": { fontWeight: 800, fontSize: 12, whiteSpace: "nowrap", bgcolor: (t) => alpha(t.palette.text.primary, t.palette.mode === "dark" ? 0.08 : 0.045) },
  "& tbody tr:last-child .MuiTableCell-root": { borderBottom: 0 },
  "& .num": { fontVariantNumeric: "tabular-nums", textAlign: "right" },
};

// Título del diálogo: nombre, una línea que explica qué se está viendo y botón para cerrar.
function Encabezado({ titulo, subtitulo, onClose }) {
  return (
    <DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 1, pr: 1.5 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="div" sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.25 }}>{titulo}</Typography>
        {subtitulo && <Typography component="div" sx={{ fontSize: 13, color: "text.secondary", fontWeight: 400, mt: 0.25 }}>{subtitulo}</Typography>}
      </Box>
      <IconButton size="small" aria-label="Cerrar" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
    </DialogTitle>
  );
}

// Grupo de registros: título a la izquierda con su cantidad, y la tabla dentro de un marco.
function Seccion({ titulo, cantidad, ayuda, children }) {
  return (
    <Box sx={{ mb: 2, "&:last-child": { mb: 0 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 800 }}>{titulo}</Typography>
        {cantidad !== undefined && <Chip size="small" label={cantidad} sx={{ height: 20, fontSize: 12, fontWeight: 700 }} />}
      </Box>
      {ayuda && <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 0.75 }}>{ayuda}</Typography>}
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <TableContainer>{children}</TableContainer>
      </Paper>
    </Box>
  );
}

const Cabecera = ({ cols, numericas = [] }) => (
  <TableHead>
    <TableRow>{cols.map((c) => <TableCell key={c || "acc"} className={numericas.includes(c) ? "num" : undefined}>{c}</TableCell>)}</TableRow>
  </TableHead>
);

// Desvincular con confirmación en la misma fila (en vez del cuadro del navegador).
function Quitar({ onConfirm }) {
  const [confirmando, setConfirmando] = useState(false);
  if (confirmando) {
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}>
        <Typography sx={{ fontSize: 12.5 }}>¿Desvincular?</Typography>
        <Button size="small" color="error" variant="contained" onClick={() => { setConfirmando(false); onConfirm(); }} sx={{ minWidth: 0, py: 0, px: 1, fontSize: 12.5 }}>Sí</Button>
        <Button size="small" onClick={() => setConfirmando(false)} sx={{ minWidth: 0, py: 0, px: 1, fontSize: 12.5 }}>No</Button>
      </Box>
    );
  }
  return (
    <Button size="small" color="error" startIcon={<LinkOffIcon sx={{ fontSize: 16 }} />} onClick={() => setConfirmando(true)} sx={{ textTransform: "none", fontSize: 12.5, py: 0, whiteSpace: "nowrap" }}>
      Desvincular
    </Button>
  );
}

// consulta_relacion_debito.php / consulta_relacion_credito.php: todo lo que está enlazado a un movimiento, con opción de desvincular.
export function RelacionesDialog({ id, onClose, onCambio }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(() => {
    if (!id) return;
    setCargando(true);
    setError("");
    getRelaciones(id).then((r) => setData(r.data)).catch((e) => setError(msg(e, "No se pudo cargar."))).finally(() => setCargando(false));
  }, [id]);
  useEffect(() => { setData(null); cargar(); }, [cargar]);

  const quitar = async (tipo, ref) => {
    try {
      await desvincularConciliacion({ tipo, id: ref, movimientoId: id });
      cargar();
      if (onCambio) onCambio();
    } catch (e) {
      setError(msg(e, "No se pudo desvincular."));
    }
  };

  const montoDe = (r) => (r.moneda === "Bolivianos" ? num(r.bolivianos) : num(r.dolares));
  const vacio = data && !data.extracto.length && !data.traspasos.length && !data.egresos.length && !data.pagos.length && !data.recibos.length;
  const totalRelacionado = data
    ? data.pagos.reduce((a, p) => a + num(p.monto), 0) + data.egresos.reduce((a, e) => a + montoDe(e), 0)
      + data.recibos.reduce((a, r) => a + montoDe(r), 0) + data.traspasos.reduce((a, t) => a + montoDe(t), 0)
    : 0;
  const resumen = data ? [
    [data.pagos.length, "pago de factura", "pagos de facturas"], [data.egresos.length, "cheque", "cheques"], [data.recibos.length, "recibo de crédito", "recibos de crédito"],
    [data.traspasos.length, "traspaso", "traspasos"], [data.extracto.length, "movimiento enlazado", "movimientos enlazados"],
  ].filter(([n]) => n > 0) : [];

  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="lg" fullWidth>
      <Encabezado titulo={`Relaciones del movimiento ${id ?? ""}`} subtitulo="Todo lo que está enlazado a este movimiento del extracto" onClose={onClose} />
      {cargando && <LinearProgress sx={{ height: 2 }} />}
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>{error}</Alert>}

        {vacio && (
          <Box sx={{ py: 4, display: "grid", justifyItems: "center", gap: 0.75, textAlign: "center" }}>
            <Box sx={{ width: 52, height: 52, borderRadius: "50%", display: "grid", placeItems: "center", color: "text.secondary", bgcolor: (t) => alpha(t.palette.text.primary, 0.07) }}><InboxOutlinedIcon /></Box>
            <Typography sx={{ fontWeight: 700 }}>Este movimiento no tiene registros enlazados</Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Cuando enlaces cheques, pagos o recibos, aparecerán aquí.</Typography>
          </Box>
        )}

        {data && !vacio && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 2 }}>
            {resumen.map(([n, uno, varios]) => <Chip key={uno} label={`${n} ${n === 1 ? uno : varios}`} variant="outlined" sx={{ fontWeight: 600 }} />)}
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Suma de lo relacionado: <b>{fmtMoney(totalRelacionado)}</b></Typography>
          </Box>
        )}

        {data && data.extracto.length > 0 && (
          <Seccion titulo="Otros movimientos del extracto" cantidad={data.extracto.length}>
            <Table size="small" sx={tablaSx}>
              <Cabecera cols={["ID", "Cód. transacción", "Débito", "Crédito", "Banco", "Fecha", ""]} numericas={["Débito", "Crédito"]} />
              <TableBody>
                {data.extracto.map((x) => (
                  <TableRow key={x.id}>
                    <TableCell>{x.id}</TableCell><TableCell>{x.cod_transaccion}</TableCell>
                    <TableCell className="num">{fmtMoney(x.debito)}</TableCell><TableCell className="num">{fmtMoney(x.credito)}</TableCell>
                    <TableCell>{x.banco}</TableCell><TableCell>{fmtFecha(x.fecha)}</TableCell>
                    <TableCell align="right"><Quitar onConfirm={() => quitar("extracto", x.id)} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Seccion>
        )}
        {data && data.traspasos.length > 0 && (
          <Seccion titulo="Traspasos internos" cantidad={data.traspasos.length}>
            <Table size="small" sx={tablaSx}>
              <Cabecera cols={["Banco origen", "Comprobante origen", "Banco destino", "Comprobante destino", "Bolivianos", "Dólares", "Moneda", "Fecha", ""]} numericas={["Bolivianos", "Dólares"]} />
              <TableBody>
                {data.traspasos.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>{t.banco_origen}</TableCell><TableCell>{t.comprobante_origen}</TableCell>
                    <TableCell>{t.banco_destino}</TableCell><TableCell>{t.comprobante_destino}</TableCell>
                    <TableCell className="num">{fmtMoney(t.bolivianos)}</TableCell><TableCell className="num">{fmtMoney(t.dolares)}</TableCell>
                    <TableCell>{t.moneda}</TableCell><TableCell>{fmtFecha(t.fecha)}</TableCell>
                    <TableCell align="right">{t.se_puede_desvincular && <Quitar onConfirm={() => quitar("traspaso", t.id)} />}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Seccion>
        )}
        {data && data.egresos.length > 0 && (
          <Seccion titulo="Cheques (débitos)" cantidad={data.egresos.length}>
            <Table size="small" sx={tablaSx}>
              <Cabecera cols={["Código", "N° cheque", "Banco", "Bolivianos", "Dólares", "Moneda", "Fecha", ""]} numericas={["Bolivianos", "Dólares"]} />
              <TableBody>
                {data.egresos.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell>{e.id}</TableCell><TableCell>{e.nocheque}</TableCell><TableCell>{e.banco}</TableCell>
                    <TableCell className="num">{fmtMoney(e.bolivianos)}</TableCell><TableCell className="num">{fmtMoney(e.dolares)}</TableCell>
                    <TableCell>{e.moneda}</TableCell><TableCell>{fmtFecha(e.fecha)}</TableCell>
                    <TableCell align="right"><Quitar onConfirm={() => quitar("egreso", e.id)} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Seccion>
        )}
        {data && data.recibos.length > 0 && (
          <Seccion titulo="Recibos de crédito" cantidad={data.recibos.length}>
            <Table size="small" sx={tablaSx}>
              <Cabecera cols={["Banco", "Comprobante", "Bolivianos", "Dólares", "Moneda", "Fecha", ""]} numericas={["Bolivianos", "Dólares"]} />
              <TableBody>
                {data.recibos.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.banco_destino}</TableCell><TableCell>{r.comprobante}</TableCell>
                    <TableCell className="num">{fmtMoney(r.bolivianos)}</TableCell><TableCell className="num">{fmtMoney(r.dolares)}</TableCell>
                    <TableCell>{r.moneda}</TableCell><TableCell>{fmtFecha(r.fecha)}</TableCell>
                    <TableCell align="right"><Quitar onConfirm={() => quitar("recibo", r.id)} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Seccion>
        )}
        {data && data.pagos.length > 0 && (
          <Seccion titulo="Pagos de facturas (créditos)" cantidad={data.pagos.length}>
            <Table size="small" sx={tablaSx}>
              <Cabecera cols={["Código", "Transacción", "Banco", "Monto", "Moneda", "Fecha", ""]} numericas={["Monto"]} />
              <TableBody>
                {data.pagos.map((p) => (
                  <TableRow key={p.cod}>
                    <TableCell>{p.cod}</TableCell><TableCell>{p.cod_banco}</TableCell><TableCell>{p.banco}</TableCell>
                    <TableCell className="num">{fmtMoney(p.monto)}</TableCell><TableCell>{p.moneda_pago}</TableCell>
                    <TableCell>{fmtFecha(p.fecha)}</TableCell>
                    <TableCell align="right"><Quitar onConfirm={() => quitar("pago", p.cod)} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Seccion>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}><Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 700 }}>Cerrar</Button></DialogActions>
    </Dialog>
  );
}

// Fila elegible con círculo: se puede tocar toda la fila y la elegida queda resaltada.
const filaElegible = (elegida) => ({
  cursor: "pointer",
  "&:hover": { bgcolor: "action.hover" },
  ...(elegida ? { bgcolor: (t) => alpha(t.palette.primary.main, 0.1), "& .MuiTableCell-root": { fontWeight: 700 } } : {}),
});

const SinDatos = ({ colSpan, texto }) => (
  <TableRow><TableCell colSpan={colSpan} sx={{ py: 3, textAlign: "center", color: "text.secondary" }}>{texto}</TableCell></TableRow>
);

// enlazar_banco.php: enlaza el movimiento con otro "No existe" del mismo extracto (p. ej. un crédito que compensa un débito).
export function EnlazarBancoDialog({ id, mes, year, onClose, onGuardado }) {
  const [data, setData] = useState(null);
  const [con, setCon] = useState(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!id) return;
    setData(null); setCon(null); setError("");
    getCandidatosBanco({ id, mes, year }).then((r) => setData(r.data)).catch((e) => setError(msg(e, "No se pudo cargar.")));
  }, [id, mes, year]);

  const guardar = async () => {
    setGuardando(true);
    try {
      await enlazarBancoConciliacion({ id, con });
      onGuardado("Movimientos enlazados.");
      onClose();
    } catch (e) {
      setError(msg(e, "No se pudo enlazar."));
    } finally {
      setGuardando(false);
    }
  };

  const tabla = (titulo, filas, campo) => (
    <Seccion titulo={titulo} cantidad={filas.length}>
      <Table size="small" sx={{ ...tablaSx, tableLayout: "fixed" }}>
        <colgroup><col style={{ width: 48 }} /><col style={{ width: 84 }} /><col /><col style={{ width: 120 }} /><col style={{ width: 96 }} /><col style={{ width: 116 }} /></colgroup>
        <Cabecera cols={["", "ID", "Cód. transacción", "Monto", "Banco", "Fecha"]} numericas={["Monto"]} />
        <TableBody>
          {!filas.length ? <SinDatos colSpan={6} texto="No hay movimientos pendientes de este tipo." /> : filas.map((r) => (
            <TableRow key={r.id} hover onClick={() => setCon(r.id)} sx={filaElegible(con === r.id)}>
              <TableCell padding="checkbox"><Radio size="small" checked={con === r.id} tabIndex={-1} /></TableCell>
              <TableCell>{r.id}</TableCell><TableCell>{r.cod_transaccion}</TableCell>
              <TableCell className="num">{fmtMoney(r[campo])}</TableCell><TableCell>{r.banco}</TableCell><TableCell>{fmtFecha(r.fecha)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Seccion>
  );

  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="sm" fullWidth>
      <Encabezado titulo={`Enlazar el movimiento ${id ?? ""} con otro del banco`} subtitulo="Elige el movimiento que lo compensa, por ejemplo un crédito que cubre un débito." onClose={onClose} />
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {!data && !error && <LinearProgress />}
        {data && <>{tabla("Débitos pendientes", data.debitos, "debito")}{tabla("Créditos pendientes", data.creditos, "credito")}</>}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Typography sx={{ flex: 1, fontSize: 13, color: "text.secondary" }}>{con ? `Elegido: movimiento ${con}` : "Elige un movimiento de la lista"}</Typography>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button variant="contained" disabled={!con || guardando} onClick={guardar} sx={{ textTransform: "none", fontWeight: 700 }}>
          {guardando ? <CircularProgress size={20} color="inherit" /> : "Enlazar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const PASOS = ["Traspaso interno", "Crédito del extracto", "Verificar y guardar"];

// Una línea de la verificación: etiqueta, monto y si coincide con el del traspaso.
function LineaVerificacion({ etiqueta, valor, ok }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.9 }}>
      {ok ? <CheckCircleIcon color="success" fontSize="small" /> : <HighlightOffIcon color="error" fontSize="small" />}
      <Typography sx={{ flex: 1, fontSize: 13.5 }}>{etiqueta}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 14, fontVariantNumeric: "tabular-nums" }}>{valor}</Typography>
    </Box>
  );
}

// enlazar_traspaso.php → seleccionar_traspaso.php → resultados_traspaso.php: se elige el traspaso interno,
// luego el crédito del extracto y sólo se guarda si los montos coinciden.
export function EnlazarTraspasoDialog({ id, debito, mes, year, onClose, onGuardado }) {
  const [paso, setPaso] = useState(1);
  const [traspasos, setTraspasos] = useState(null);
  const [creditos, setCreditos] = useState(null);
  const [traspaso, setTraspaso] = useState(null);
  const [credito, setCredito] = useState(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!id) return;
    setPaso(1); setTraspaso(null); setCredito(null); setCreditos(null); setError(""); setTraspasos(null);
    getTraspasosLibres(year).then((r) => setTraspasos(r.data)).catch((e) => setError(msg(e, "No se pudo cargar.")));
  }, [id, year]);

  const irPaso2 = () => {
    setPaso(2);
    getCreditosTraspaso({ mes, year }).then((r) => setCreditos(r.data)).catch((e) => setError(msg(e, "No se pudo cargar.")));
  };

  const monto = traspaso?.monto ?? 0;
  const debitoOk = !!traspaso && Math.abs(debito - monto) < 0.005;
  const creditoOk = !!traspaso && !!credito && Math.abs(credito.monto - monto) < 0.005;
  const coincide = debitoOk && creditoOk;

  const guardar = async () => {
    setGuardando(true);
    setError("");
    try {
      await enlazarTraspasoConciliacion({ debitoId: id, creditoId: credito.id, traspasoId: traspaso.id });
      onGuardado("Traspaso enlazado.");
      onClose();
    } catch (e) {
      setError(msg(e, "No se pudo enlazar."));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={!!id} onClose={onClose} maxWidth="md" fullWidth>
      <Encabezado titulo="Enlazar con un traspaso interno" subtitulo={`Movimiento ${id ?? ""} · Débito ${fmtMoney(debito)}`} onClose={onClose} />
      <Box sx={{ px: 3, pt: 1.5, pb: 1.5, borderBottom: 1, borderColor: "divider" }}>
        <Stepper activeStep={paso - 1} alternativeLabel>
          {PASOS.map((p) => <Step key={p}><StepLabel>{p}</StepLabel></Step>)}
        </Stepper>
      </Box>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {paso === 1 && (
          <Seccion titulo="Elige el traspaso interno" cantidad={traspasos ? traspasos.length : undefined} ayuda="Se muestra el comprobante de origen de cada traspaso que todavía no está enlazado.">
            {!traspasos ? <Box sx={{ p: 2 }}><LinearProgress /></Box> : (
              <Table size="small" sx={tablaSx}>
                <Cabecera cols={["", "Comprobante", "N° cheque", "Fecha", "Monto", "Moneda", "Banco origen"]} numericas={["Monto"]} />
                <TableBody>
                  {!traspasos.length ? <SinDatos colSpan={7} texto="No hay traspasos pendientes de enlazar." /> : traspasos.map((t) => (
                    <TableRow key={t.id} hover onClick={() => setTraspaso(t)} sx={filaElegible(traspaso?.id === t.id)}>
                      <TableCell padding="checkbox"><Radio size="small" checked={traspaso?.id === t.id} tabIndex={-1} /></TableCell>
                      <TableCell>{t.comprobante_origen}</TableCell><TableCell>{t.num_cheque_origen}</TableCell>
                      <TableCell>{fmtFecha(t.fecha)}</TableCell><TableCell className="num">{fmtMoney(t.monto)}</TableCell>
                      <TableCell>{t.moneda}</TableCell><TableCell>{t.banco_origen}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Seccion>
        )}
        {paso === 2 && (
          <Seccion
            titulo="Elige el crédito del extracto" cantidad={creditos ? creditos.length : undefined}
            ayuda={`Debe ser el que recibe el traspaso (destino ${traspaso?.comprobante_destino || "—"}) por ${fmtMoney(monto)} ${traspaso?.moneda || ""}.`}
          >
            {!creditos ? <Box sx={{ p: 2 }}><LinearProgress /></Box> : (
              <Table size="small" sx={tablaSx}>
                <Cabecera cols={["", "Cód. transacción", "Banco", "Fecha", "Crédito"]} numericas={["Crédito"]} />
                <TableBody>
                  {!creditos.length ? <SinDatos colSpan={5} texto="No hay créditos pendientes en este mes." /> : creditos.map((c) => (
                    <TableRow key={c.id} hover onClick={() => setCredito(c)} sx={filaElegible(credito?.id === c.id)}>
                      <TableCell padding="checkbox"><Radio size="small" checked={credito?.id === c.id} tabIndex={-1} /></TableCell>
                      <TableCell>{c.cod_transaccion}</TableCell><TableCell>{c.banco}</TableCell>
                      <TableCell>{fmtFecha(c.fecha)}</TableCell><TableCell className="num">{fmtMoney(c.monto)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Seccion>
        )}
        {paso === 3 && traspaso && credito && (
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.5 }}>Verifica que los montos coincidan</Typography>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
              Traspaso {traspaso.comprobante_origen} → {traspaso.comprobante_destino}: <b>{fmtMoney(monto)} {traspaso.moneda}</b>
            </Typography>
            <Paper variant="outlined" sx={{ px: 1.5, "& > div + div": { borderTop: 1, borderColor: "divider" } }}>
              <LineaVerificacion etiqueta={`Débito del extracto (movimiento ${id})`} valor={fmtMoney(debito)} ok={debitoOk} />
              <LineaVerificacion etiqueta={`Crédito del extracto (${credito.cod_transaccion})`} valor={fmtMoney(credito.monto)} ok={creditoOk} />
            </Paper>
            {coincide
              ? <Alert severity="success" sx={{ mt: 1.5 }}>Los montos coinciden. Ya puedes guardar el enlace.</Alert>
              : <Alert severity="error" sx={{ mt: 1.5 }}>El monto no coincide con el del traspaso. Vuelve atrás y elige otro traspaso o crédito.</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Box sx={{ flex: 1 }} />
        {paso > 1 && <Button onClick={() => setPaso(paso - 1)} sx={{ textTransform: "none" }}>Atrás</Button>}
        {paso === 1 && <Button variant="contained" disabled={!traspaso} onClick={irPaso2} sx={{ textTransform: "none", fontWeight: 700 }}>Siguiente</Button>}
        {paso === 2 && <Button variant="contained" disabled={!credito} onClick={() => setPaso(3)} sx={{ textTransform: "none", fontWeight: 700 }}>Siguiente</Button>}
        {paso === 3 && (
          <Button variant="contained" disabled={!coincide || guardando} onClick={guardar} sx={{ textTransform: "none", fontWeight: 700 }}>
            {guardando ? <CircularProgress size={20} color="inherit" /> : "Guardar enlace"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
